import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { createUseStyles } from 'react-jss'
import Flickity from 'react-flickity-component'
import gsap from 'gsap'
import { LinkWithCursorHoverEffect } from '../Link'
import isEmpty from 'lodash/isEmpty'
import theme from '../../style/theme'
import { span } from '../../style/span'
import { vw } from '../../style/vw'
import cn from 'classnames'
import SplitWordsAnimation from '../SplitWordsAnimation'
import arrow from '../../images/arrow.svg'
import ColorContext from '../ColorContext'
import { useSelector } from 'react-redux'
import { isCurrentBreakpointMobile } from '../../redux/slices/layout'

// const preventVerticalScrolling = (flickity) => {
//   // This will prevent the vertical scrolling when scrolling in flickity
//   flickity.on('dragStart', () => { document.ontouchmove = e => e.preventDefault() })
//   flickity.on('dragEnd', () => { document.ontouchmove = () => true })
// }

const FLICKITY_OPTIONS = {
  cellAlign: 'left',
  pageDots: false,
  contain: true,
  freeScroll: true,
  prevNextButtons: false, // Disable default buttons to use custom ones
  adaptiveHeight: false, // Fixed height for slides
  groupCells: false, // Group cells to improve UX
  wrapAround: false, // No infinite scrolling
  selectedAttraction: 0.015, // Smoother transitions
  friction: 0.25, // Smoother transitions
  percentPosition: true,
  autoPlay: 0 // No auto-scrolling
}

const Slider = ({ className, children, title, description, link, linkText, flickityOptions = FLICKITY_OPTIONS, onChange, withShadow = false, ...rest }) => {
  const flickityRef = useRef()
  const { backgroundColor } = useContext(ColorContext)
  const classes = useStyles({ backgroundColor })
  const onChangeRef = useRef(onChange)
  const [currentIndex, setCurrentIndex] = useState(0)
  const isMobile = useSelector(isCurrentBreakpointMobile)

  useEffect(() => {
    onChangeRef.current = onChange
  })

  const handleOnChange = useCallback((index) => {
    setCurrentIndex(index)
    // eslint-disable-next-line no-unused-expressions
    onChangeRef.current?.(index)
  }, [])

  const setFlickityRef = useCallback((ref) => {
    // eslint-disable-next-line no-unused-expressions
    flickityRef.current?.off('change', handleOnChange)
    flickityRef.current = ref
    // eslint-disable-next-line no-unused-expressions
    flickityRef.current?.on('change', handleOnChange)

    gsap.set(ref.element, { display: 'block', overflow: 'visible' })
    // // Flickity consumes the mouse move events so we need to create a customer event so the custom mouse
    // // cursor continues to follow the mouse cursor
    // ref.on('dragMove', (event, pointer, moveVector) => {
    //   const dragMoveEvent = new window.CustomEvent('flickity-dragMove', { detail: event })
    //   window.dispatchEvent(dragMoveEvent)
    // })
    // preventVerticalScrolling(ref)
    // // Update state when slide changes
    // ref.on('change', (index) => {
    //   if (onChange) {
    //     console.log('index', index)
    //     onChange(index)
    //   }
    // })
  }, [])

  const handlePrevious = useCallback(() => {
    if (flickityRef.current) {
      flickityRef.current.previous()
    }
  }, [])

  const handleNext = useCallback(() => {
    if (flickityRef.current) {
      flickityRef.current.next()
    }
  }, [])

  const isPrevDisabled = currentIndex === 0
  const isNextDisabled = currentIndex === (isMobile ? flickityRef.current?.slides?.length - 1 : flickityRef.current?.slides?.length - 3)

  return (
    <section className={cn(className, classes.section)}>
      <div className={classes.mobileTitleContainer}>
        {title && <SplitWordsAnimation><h3 className={classes.mobileTitle}>{title}</h3></SplitWordsAnimation>}
        {description && <p className={classes.mobileDescription}>{description}</p>}
        {!isEmpty(link) && <LinkWithCursorHoverEffect link={link} className={classes.link}>{linkText}</LinkWithCursorHoverEffect>}
      </div>
      <div className={classes.sliderContainer}>
        <SplitWordsAnimation>
          <h3 className={classes.title}>{title}</h3>
        </SplitWordsAnimation>
        <Flickity
          options={flickityOptions}
          disableImagesLoaded
          // reloadOnUpdate
          static
          className={classes.flickity}
          flickityRef={setFlickityRef}
          {...rest}
        >
          {description && (
            <div className={classes.titleContainer}>
              <p className={classes.description}>{description}</p>
              {!isEmpty(link) && <LinkWithCursorHoverEffect link={link} className={classes.link}>{linkText}</LinkWithCursorHoverEffect>}
            </div>
          )}
          {children}
        </Flickity>
        {withShadow && <div className={classes.shadow} />}
        <div>
          <button
            className={`${classes.navButton} ${classes.prevButton} ${isPrevDisabled ? 'disabled' : ''}`}
            onClick={handlePrevious}
            aria-label='Previous slide'
          >
            <img src={arrow} alt='Previous slide' className={classes.prevArrowIcon} />
          </button>
          <button
            className={`${classes.navButton} ${classes.nextButton} ${isNextDisabled ? 'disabled' : ''}`}
            onClick={handleNext}
            aria-label='Next slide'
          >
            <img src={arrow} alt='Next slide' className={classes.nextArrowIcon} />
          </button>
        </div>
      </div>
    </section>
  )
}

const useStyles = createUseStyles({
  section: {
    paddingTop: span(3),
    paddingLeft: span(1),
    paddingRight: span(1),
    [theme.breakpoints.up('md')]: {
      paddingTop: span(3, 'md'),
      paddingLeft: span(1, 'md'),
      paddingRight: span(1, 'md')
    },
    overflowX: 'hidden',
    position: 'relative',
    '&:hover $navButton': {
      opacity: 1,
      '&.disabled': {
        opacity: '0!important',
        cursor: 'not-allowed'
      }
    }
  },
  flickity: {
    outline: 'none',
    whiteSpace: 'pre-wrap',
    display: 'flex',
    padding: [20, 0],
    '& > *': {
      flexShrink: 0
    }
  },
  mobileTitleContainer: {
    [theme.breakpoints.up('md')]: {
      display: 'none'
    }
  },
  mobileTitle: {
    whiteSpace: 'pre-wrap',
    display: 'block',
    fontSize: vw(30),
    lineHeight: 1.1,
    [theme.breakpoints.up('md')]: {
      display: 'none'
    }
  },
  mobileDescription: {
    display: 'block',
    position: 'relative',
    fontSize: vw(14),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      display: 'none'
    }
  },
  titleContainer: {
    display: 'none',
    position: 'relative',
    fontSize: '16px',
    width: '320px',
    height: 'fit-content',
    transition: 'opacity 0.5s ease',
    [theme.breakpoints.up('md')]: {
      fontSize: vw(16, 'desktop'),
      display: 'block',
      width: vw(320, 'desktop'),
      height: 'fit-content',
      padding: '0px 20px 0px 0px'
    },
    '&:not(.is-selected)': {
      opacity: 0
    }
  },
  title: {
    fontSize: vw(30),
    marginBottom: vw(28),
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
      fontSize: vw(48, 'desktop'),
      marginBottom: vw(58, 'desktop')
    }
  },
  link: {
    display: 'inline-block',
    textTransform: 'uppercase',
    textDecoration: 'none',
    position: 'relative',
    margin: [0, 0, vw(40), 0],
    fontSize: '16px',
    ...theme.link(),
    [theme.breakpoints.up('md')]: {
      fontSize: vw(16, 'desktop'),
      margin: [0, 0, vw(40, 'desktop'), 0]
    }
  },
  titleContainerStatic: {
    display: 'block',
    marginBottom: '20px',
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      marginBottom: '30px',
      paddingLeft: span(1, 'md')
    }
  },
  titleStatic: {
    whiteSpace: 'normal',
    display: 'block',
    color: theme.colors.primary,
    fontSize: '24px',
    fontWeight: 'bold',
    textAlign: 'left',
    transform: 'none',
    position: 'static',
    [theme.breakpoints.up('md')]: {
      fontSize: '28px'
    }
  },
  sliderContainer: {
    position: 'relative'
  },
  navButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: 'calc(50% )',
    transform: 'translateY(-50%)',
    width: '30px',
    height: '30px',
    backgroundColor: theme.colors.primary,
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    padding: '1px',
    opacity: 1,
    transition: 'opacity 0.3s ease, background-color 0.2s ease, transform 0.2s ease',
    zIndex: 10,

    '&:hover': {
      backgroundColor: theme.colors.primary,
      transform: 'translateY(-50%) scale(1.1)'
    },

    [theme.breakpoints.down('md')]: {
      '&.disabled': {
        opacity: 0,
        cursor: 'not-allowed'
      }
    },

    [theme.breakpoints.up('md')]: {
      opacity: 0,
      width: '40px',
      height: '40px',
      top: 'calc(50%)'
    }
  },
  prevButton: {
    left: span(-1),
    [theme.breakpoints.up('md')]: {
      left: span(-1, 'md')
    }
  },
  nextButton: {
    right: span(-1),
    [theme.breakpoints.up('md')]: {
      right: span(-1, 'md')
    }
  },
  prevArrowIcon: {
    display: 'block',
    width: '20px',
    height: '20px',
    transform: 'rotate(180deg)', // Rotate arrow to point left
    '& path': {
      fill: 'white'
    },

    [theme.breakpoints.up('md')]: {
      width: '35px',
      height: '35px'
    }
  },
  nextArrowIcon: {
    display: 'block',
    width: '20px',
    height: '30px',
    transform: 'rotate(0deg)', // Arrow pointing right (no rotation needed)
    '& path': {
      fill: 'white'
    },

    [theme.breakpoints.up('md')]: {
      width: '35px',
      height: '35px'
    }
  },
  shadow: {
    display: 'none',
    transition: 'all 0.5s ease-in-out',
    '&.hide': {
      transform: 'translateX(100%)'
    },
    [theme.breakpoints.up('md')]: {
      background: ({ backgroundColor }) => `linear-gradient(to right, ${backgroundColor}00, ${backgroundColor})`,
      position: 'absolute',
      display: 'block',
      top: 0,
      bottom: 0,
      right: span(-1, 'md'),
      width: vw(320, 'desktop'),
      pointerEvent: 'none',
      zIndex: 10
    }
  }
}, { name: 'Slider' })

export default Slider
